import { deserialize as deserializeJsonApi } from 'deserialize-json-api';
import { Links } from '@/src/types/generic';

type JsonApiOptions = { flatten?: Array<string>; includeType?: boolean };

type JsonApiRelationType = { data: { type: string; id: string } } | { data: Array<{ type: string; id: string }> };

export type JsonApiEntity<T = Record<string, unknown>> = {
  id: string | null;
  type: string;
  attributes: T;
  relationships?: Record<string, JsonApiRelationType>;
  links?: {
    self: string;
  };
};

export type JsonApiEntityWithId<T = Record<string, unknown>> = JsonApiEntity<T> & { id: string };

export type SingleApiFormat<T = JsonApiEntity, I = JsonApiEntity> = {
  data: JsonApiEntity<T>;
  included?: Array<I>;
  links?: {
    self: string;
  };
};

export type SingleApiFormatWithETag<T = JsonApiEntity, I = JsonApiEntity> = SingleApiFormat<T, I> & {
  etag: string;
};

export type SingleApiFormatWithId<T = JsonApiEntityWithId, I = JsonApiEntityWithId> = {
  data: JsonApiEntityWithId<T>;
  included?: Array<I>;
  links?: {
    self: string;
  };
};

export type CollectionApiFormat<T = JsonApiEntity, I = JsonApiEntity, M = Record<string, unknown>> = {
  data: Array<JsonApiEntity<T>>;
  included?: Array<I>;
  links?: Links;
  meta?: M;
};

export type CollectionApiFormatWithId<T = JsonApiEntity, I = JsonApiEntity> = {
  data: Array<JsonApiEntity<T> & { id: string }>;
} & CollectionApiFormat<T, I>;

export type DeserializedObject<T extends Record<string, unknown>> = T & { type?: string; id: string; links?: Links };

/**
 * We use this method as a wrapper around deserialize-json-api to add type-casting
 */
export function deserialize<T extends Record<string, unknown>>(
  input: SingleApiFormat<Record<string, unknown>>,
  options?: JsonApiOptions,
): DeserializedObject<T> {
  const deserializedEntity = deserializeJsonApi(input, { transformKeys: 'camelCase', flatten: false, ...options });
  return deserializedEntity.data as DeserializedObject<T>;
}

export function deserializeCollection<T extends Record<string, unknown>>(
  input: CollectionApiFormat<unknown>,
  transformKeys: 'camelCase' | 'kebabCase' = 'camelCase',
): Array<DeserializedObject<T>> {
  return deserializeJsonApi(input, { transformKeys }).data;
}
